import { useSelector } from 'react-redux'
import { Redirect, Switch } from 'react-router'

import { ProtectedRoute } from '@webapp/app/components'
import { useModuleConfig } from '@webapp/app/config/useModuleConfig'
import { uuidRegExp } from '@webapp/config/react-router'
import { makeSelectFeatureSwitches } from '@webapp/core/stores/global'
import { useBillsPermissions, useFeatureFlag } from '@webapp/hooks'
import { useAppRoutes } from '@webapp/hooks/useAppRoutes'
import { BillApprovalsPage } from '@webapp/modules/accounts-payable/pages/BillApprovalsPage'
import {
  BillCreatePage,
  BillListPage,
  BillRequestDetailsPage,
} from '@webapp/pages'
import { BillApprovalDetailsPage } from '@webapp/pages/BillApprovalDetailsPage'
import { BillListPageStatuses } from '@webapp/pages/BillListPage'
import { Permissions } from '@webapp/platform/iam'

const BILL_APPROVALS_PAGE_STATUSES = ['pending-approval', 'approval-history']

export const BillRouter = () => {
  const { url, brp } = useAppRoutes()

  const {
    routes: { enableEverythingButExpenseApRoutes },
  } = useModuleConfig()

  const FT_7515_FIX_CREATE_BILL_PERMISSION_ACCESS = useFeatureFlag(
    'FT_7515_FIX_CREATE_BILL_PERMISSION_ACCESS'
  )

  const PSERV_346_BASE_MIGRATION = useFeatureFlag('PSERV_346_BASE_MIGRATION')

  const { canViewBills } = useBillsPermissions()

  const { disable_ap_navigation: disableAPNavigation } = useSelector(
    makeSelectFeatureSwitches()
  )

  const canNavigateToCreateBillPage = FT_7515_FIX_CREATE_BILL_PERMISSION_ACCESS
    ? canViewBills && enableEverythingButExpenseApRoutes
    : enableEverythingButExpenseApRoutes

  return (
    <Switch>
      {disableAPNavigation && (
        <Redirect path='/bills' to={url('DashboardPage')} />
      )}

      <Redirect
        exact
        from={brp('BillListPage', {
          status: '',
        })}
        to={url('BillListPage', {
          status: BillListPageStatuses.DRAFT,
        })}
      />

      <ProtectedRoute
        path={brp('BillRequestDetailsPage', {
          uuid: `:uuid(${uuidRegExp.source})`,
        })}
        component={BillRequestDetailsPage}
      />

      {enableEverythingButExpenseApRoutes && (
        <ProtectedRoute
          path={brp('BillApprovalDetailsPage', {
            uuid: `:uuid(${uuidRegExp.source})`,
          })}
          component={BillApprovalDetailsPage}
        />
      )}

      {/* PSERV_346: flag-off falls back to the Angular bill approvals page. */}
      {!PSERV_346_BASE_MIGRATION && (
        <Redirect
          path={brp('BillApprovalsPage', {
            status: `:status(${BILL_APPROVALS_PAGE_STATUSES.join('|')})`,
          })}
          to='/#/bills/approvals'
        />
      )}

      <ProtectedRoute
        path={brp('BillApprovalsPage', {
          status: `:status(${BILL_APPROVALS_PAGE_STATUSES.join('|')})`,
        })}
        component={BillApprovalsPage}
        permissions={[Permissions.APPROVE_BILL]}
        featureFlags={['PSERV_346_BASE_MIGRATION']}
      />

      {/*
       * Empty or unknown status falls through the constrained route above and
       * redirects to the default tab. Placed after the uuid-constrained
       * BillApprovalDetailsPage route so bill detail UUIDs are never captured.
       */}
      {PSERV_346_BASE_MIGRATION && (
        <Redirect
          from={brp('BillApprovalsPage', { status: ':status?' })}
          to={url('BillApprovalsPage', { status: 'pending-approval' })}
        />
      )}
      {!PSERV_346_BASE_MIGRATION && (
        <Redirect
          from={brp('BillApprovalsPage', { status: ':status?' })}
          to='/#/bills/approvals'
        />
      )}

      <ProtectedRoute
        path={brp('BillListPage', {
          status: ':status?',
        })}
        component={BillListPage}
        navigationPermissions={['ap__manage_bills']}
      />

      {canNavigateToCreateBillPage && (
        <ProtectedRoute
          path={brp('BillCreatePage', {
            uuid: ':uuid?',
          })}
          component={BillCreatePage}
        />
      )}

      <Redirect to={url('DashboardPage')} />
    </Switch>
  )
}
